import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFiltrosPersistidos } from "./useFiltrosPersistidos";
import { primeiroDiaMesAtualIso, hojeIso } from "../../utils/solicitacoes/datas";

/**
 * Hook genérico para as telas "Dashboard de Processo": período (Data Inicial/Data Final) sempre
 * obrigatório e nunca dispara busca sozinho — nenhum filtro dispara chamada ao digitar/trocar,
 * só ao chamar `pesquisar()` (botão "Pesquisar"). Busca em paralelo a listagem (tabela) e os
 * indicadores/gráficos, sempre com o mesmo conjunto de filtros aplicado aos dois, para nunca ter
 * tabela e gráfico representando períodos diferentes.
 *
 * `service` precisa expor `relatorio(filtros)` e `dashboard(filtros)`, ambos recebendo um objeto
 * plano (mesmo formato usado por `PromocaoFuncionarioService`/`AnaliseLimitePrazoService` etc.).
 */
export function useDashboardProcesso({ service, chaveFiltros, filtrosIniciaisExtra = {}, prepararFiltros = f => f }) {
    const filtrosIniciais = {
        dataInicial: primeiroDiaMesAtualIso(),
        dataFinal: hojeIso(),
        ...filtrosIniciaisExtra,
    };

    const [filtros, setFiltros, limparFiltrosBase] = useFiltrosPersistidos(chaveFiltros, filtrosIniciais);

    // Filtros persistidos de antes do período ser obrigatório podem ter vindo sem data.
    useEffect(() => {
        if (!filtros.dataInicial || !filtros.dataFinal) {
            setFiltros(prev => ({
                ...prev,
                dataInicial: prev.dataInicial || filtrosIniciais.dataInicial,
                dataFinal: prev.dataFinal || filtrosIniciais.dataFinal,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [filtrosAplicados, setFiltrosAplicados] = useState(() => prepararFiltros({
        ...filtros,
        dataInicial: filtros.dataInicial || filtrosIniciais.dataInicial,
        dataFinal: filtros.dataFinal || filtrosIniciais.dataFinal,
    }));

    const [linhas, setLinhas] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([service.relatorio(filtrosAplicados), service.dashboard(filtrosAplicados)])
            .then(([resLinhas, resDashboard]) => {
                setLinhas(resLinhas.data);
                setDashboard(resDashboard.data);
            })
            .catch(error => {
                console.error("Erro ao carregar dashboard", error);
                toast.error("Erro ao carregar os dados do dashboard");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtrosAplicados]);

    function pesquisar() {
        const aplicados = prepararFiltros({ ...filtros });
        if (!aplicados.dataInicial || !aplicados.dataFinal) {
            toast.error("Informe a data inicial e a data final para realizar a consulta.");
            return;
        }
        if (aplicados.dataInicial > aplicados.dataFinal) {
            toast.error("A data inicial não pode ser maior que a data final.");
            return;
        }
        setFiltrosAplicados(aplicados);
    }

    function limparFiltros() {
        limparFiltrosBase();
        setFiltrosAplicados(prepararFiltros(filtrosIniciais));
    }

    return { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading };
}
